import React, { useEffect, useRef, useState } from 'react';
import {
  Alert, Box, Button, Checkbox, Dialog, DialogActions,
  DialogContent, DialogTitle, FormControlLabel, Typography,
} from '@mui/material';
import { API } from '../../../config';

const usd = (cents) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency', currency: 'USD',
  }).format(cents / 100);

const validQuote = (q, orderId, amountCents) => {
  if (!q || typeof q !== 'object') return false;

  const gross = q.carrierRateCents;
  const fee = q.estimatedCarrierProcessingFeeCents;
  const net = q.estimatedCarrierNetCents;

  return (
    q.currency === 'usd' &&
    q.feePayer === 'carrier' &&
    q.feePolicy === 'carrier_pays_v1' &&
    q.disclosureVersion === 'carrier_processing_v1' &&
    q.isEstimate === true &&
    q.processingFeeCents === 0 &&
    Number.isSafeInteger(gross) && gross > 0 &&
    Number.isSafeInteger(fee) && fee >= 0 &&
    Number.isSafeInteger(net) && net > 0 &&
    net === gross - fee && q.totalCents === gross &&
    typeof q.disclosure === 'string' && q.disclosure.trim().length > 0 &&
    typeof q.estimateBasis === 'string' && q.estimateBasis.length > 0 &&
    (amountCents === undefined
      ? q.orderId === orderId
      : q.orderId === null && gross === amountCents)
  );
};

export default function CarrierFeeReviewDialog({
  token, orderId, amountCents, confirmLabel, onCancel, onConfirm,
}) {
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [accepted, setAccepted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const sending = useRef(false);

  const query = amountCents === undefined
    ? `orderId=${orderId}`
    : `amountCents=${amountCents}`;

  const quote = result?.query === query && result?.token === token &&
    result?.orderId === orderId ? result.data : null;

  useEffect(() => {
    let active = true;
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 15000);

    setResult(null);
    setAccepted(false);
    setError('');
    setLoading(true);

    const loadQuote = async () => {
      try {
        if (!token) throw new Error('Please sign in again.');

        if (
          !Number.isSafeInteger(orderId) || orderId <= 0 ||
          (amountCents !== undefined &&
            (!Number.isSafeInteger(amountCents) || amountCents <= 0))
        ) {
          throw new Error('Invalid load or price.');
        }

        const response = await fetch(
          `${API}/api/courier/fees/preview?${query}`,
          {
            headers: { Authorization: `Bearer ${token}` },
            signal: controller.signal,
            cache: 'no-store',
          }
        );

        const data = await response.json().catch(() => null);

        if (!response.ok) {
          throw new Error(
            data?.message || data?.error?.message ||
            (typeof data?.error === 'string' ? data.error : '') ||
            `Unable to load fee preview (${response.status}).`
          );
        }

        if (!validQuote(data, orderId, amountCents)) {
          throw new Error('The fee preview is incomplete. Please try again.');
        }

        if (active) setResult({ data, query, token, orderId });
      } catch (err) {
        if (active) {
          setError(
            err.name === 'AbortError'
              ? 'Fee preview timed out. Close this review and try again.'
              : err.message || 'Unable to load fee preview.'
          );
        }
      } finally {
        clearTimeout(timeout);
        if (active) setLoading(false);
      }
    };

    loadQuote();

    return () => {
      active = false;
      clearTimeout(timeout);
      controller.abort();
    };
  }, [query, token, orderId, amountCents]);

  const confirm = async () => {
    if (!quote || loading || !accepted || sending.current || error) return;

    sending.current = true;
    setSubmitting(true);

    try {
      await onConfirm({
        accepted: true,
        feePolicy: quote.feePolicy,
        disclosureVersion: quote.disclosureVersion,
        amountCents: quote.carrierRateCents,
        estimatedProcessingFeeCents: quote.estimatedCarrierProcessingFeeCents,
        estimatedNetCents: quote.estimatedCarrierNetCents,
        estimateBasis: quote.estimateBasis,
      });
    } catch (err) {
      setAccepted(false);
      setError(err.message || 'Unable to submit. Close and review again.');
    } finally {
      sending.current = false;
      setSubmitting(false);
    }
  };

  const cancel = () => {
    if (!sending.current) onCancel();
  };

  return (
    <Dialog
      open
      fullWidth
      maxWidth="sm"
      onClose={cancel}
      disableEscapeKeyDown={submitting}
      aria-labelledby="carrier-fee-review-title"
    >
      <DialogTitle id="carrier-fee-review-title">
        Review price and processing fees
      </DialogTitle>

      <DialogContent>
        <Typography sx={{ mb: 2 }}>Load ORD-{orderId}</Typography>

        {loading && (
          <Typography role="status">Loading fee estimate…</Typography>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        )}

        {quote && !loading && (
          <>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: '1fr auto',
                gap: 1,
                mb: 2,
              }}
            >
              <Typography>Transportation price</Typography>
              <Typography>{usd(quote.carrierRateCents)}</Typography>

              <Typography>Estimated processing deduction</Typography>
              <Typography>
                −{usd(quote.estimatedCarrierProcessingFeeCents)}
              </Typography>

              <Typography sx={{ fontWeight: 700 }}>
                Estimated earnings after processing
              </Typography>
              <Typography sx={{ fontWeight: 700 }}>
                {usd(quote.estimatedCarrierNetCents)}
              </Typography>
            </Box>

            <Typography variant="body2" sx={{ mb: 2 }}>
              {quote.disclosure}
            </Typography>

            <FormControlLabel
              control={
                <Checkbox
                  checked={accepted}
                  onChange={(event) => setAccepted(event.target.checked)}
                  disabled={submitting || Boolean(error)}
                />
              }
              label="I understand that the actual payment processing fee will be deducted from my payment and may differ from this estimate."
            />
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={cancel} disabled={submitting}>
          Back
        </Button>

        <Button
          variant="contained"
          onClick={confirm}
          disabled={
            loading || !quote || !accepted ||
            submitting || Boolean(error)
          }
        >
          {submitting ? 'Submitting…' : confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}